import { createReducer, on } from '@ngrx/store';
import { APIKey, APIKeyFilters, APIKeyUsageStats, CreateAPIKeyResponse, RotateAPIKeyResponse } from '../models/api-key.models';
import * as ApiKeysActions from './api-keys.actions';

/**
 * API Keys State Interface
 */
export interface ApiKeysState {
  // API Keys list
  apiKeys: APIKey[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;

  // Filters
  filters: APIKeyFilters;

  // Selected API key
  selectedApiKey: APIKey | null;

  // Newly created key (with actual key value - shown only once!)
  newlyCreatedKey: CreateAPIKeyResponse | null;

  // Rotated key (with new key value - shown only once!)
  rotatedKey: RotateAPIKeyResponse | null;

  // Usage stats
  usageStats: APIKeyUsageStats | null;

  // Loading states
  loading: boolean;
  loadingApiKey: boolean;
  loadingUsageStats: boolean;
  creating: boolean;
  updating: boolean;
  revoking: boolean;
  deleting: boolean;
  regenerating: boolean;
  rotating: boolean;

  // Error states
  error: string | null;
  createError: string | null;
  updateError: string | null;
  revokeError: string | null;
  deleteError: string | null;
  rotateError: string | null;
  usageStatsError: string | null;
}

/**
 * Initial State
 */
export const initialState: ApiKeysState = {
  apiKeys: [],
  total: 0,
  page: 1,
  page_size: 20,
  total_pages: 0,
  has_next: false,
  has_prev: false,
  filters: {
    page: 1,
    page_size: 20,
    include_revoked: true
  },
  selectedApiKey: null,
  newlyCreatedKey: null,
  rotatedKey: null,
  usageStats: null,
  loading: false,
  loadingApiKey: false,
  loadingUsageStats: false,
  creating: false,
  updating: false,
  revoking: false,
  deleting: false,
  regenerating: false,
  rotating: false,
  error: null,
  createError: null,
  updateError: null,
  revokeError: null,
  deleteError: null,
  rotateError: null,
  usageStatsError: null
};

/**
 * API Keys Reducer
 */
export const apiKeysReducer = createReducer(
  initialState,

  // Load API Keys
  on(ApiKeysActions.loadApiKeys, (state, { filters }) => ({
    ...state,
    loading: true,
    error: null,
    filters: { ...state.filters, ...filters }
  })),

  on(ApiKeysActions.loadApiKeysSuccess, (state, { data }) => ({
    ...state,
    loading: false,
    apiKeys: data.keys,
    total: data.total,
    page: data.page,
    page_size: data.page_size,
    total_pages: data.total_pages,
    has_next: data.has_next,
    has_prev: data.has_prev,
    error: null
  })),

  on(ApiKeysActions.loadApiKeysFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Load Single API Key
  on(ApiKeysActions.loadApiKey, (state) => ({
    ...state,
    loadingApiKey: true,
    error: null
  })),

  on(ApiKeysActions.loadApiKeySuccess, (state, { apiKey }) => ({
    ...state,
    loadingApiKey: false,
    selectedApiKey: apiKey,
    error: null
  })),

  on(ApiKeysActions.loadApiKeyFailure, (state, { error }) => ({
    ...state,
    loadingApiKey: false,
    error
  })),

  // Create API Key
  on(ApiKeysActions.createApiKey, (state) => ({
    ...state,
    creating: true,
    createError: null,
    newlyCreatedKey: null
  })),

  on(ApiKeysActions.createApiKeySuccess, (state, { response }) => ({
    ...state,
    creating: false,
    newlyCreatedKey: response,
    apiKeys: [response.key_info, ...state.apiKeys],
    total: state.total + 1,
    createError: null
  })),

  on(ApiKeysActions.createApiKeyFailure, (state, { error }) => ({
    ...state,
    creating: false,
    createError: error
  })),

  // Update API Key
  on(ApiKeysActions.updateApiKey, (state) => ({
    ...state,
    updating: true,
    updateError: null
  })),

  on(ApiKeysActions.updateApiKeySuccess, (state, { apiKey }) => ({
    ...state,
    updating: false,
    apiKeys: state.apiKeys.map(key => key.id === apiKey.id ? apiKey : key),
    selectedApiKey: state.selectedApiKey?.id === apiKey.id ? apiKey : state.selectedApiKey,
    updateError: null
  })),

  on(ApiKeysActions.updateApiKeyFailure, (state, { error }) => ({
    ...state,
    updating: false,
    updateError: error
  })),

  // Revoke API Key
  on(ApiKeysActions.revokeApiKey, (state) => ({
    ...state,
    revoking: true,
    revokeError: null
  })),

  on(ApiKeysActions.revokeApiKeySuccess, (state, { keyId }) => ({
    ...state,
    revoking: false,
    apiKeys: state.filters.include_revoked
      ? state.apiKeys.map(key => key.id === keyId ? { ...key, is_active: false, revoked_at: new Date().toISOString() } : key)
      : state.apiKeys.filter(key => key.id !== keyId),
    total: state.filters.include_revoked ? state.total : state.total - 1,
    revokeError: null
  })),

  on(ApiKeysActions.revokeApiKeyFailure, (state, { error }) => ({
    ...state,
    revoking: false,
    revokeError: error
  })),

  // Delete API Key (Permanent)
  on(ApiKeysActions.deleteApiKey, (state) => ({
    ...state,
    deleting: true,
    deleteError: null
  })),

  on(ApiKeysActions.deleteApiKeySuccess, (state, { keyId }) => ({
    ...state,
    deleting: false,
    apiKeys: state.apiKeys.filter(key => key.id !== keyId),
    total: state.total - 1,
    deleteError: null
  })),

  on(ApiKeysActions.deleteApiKeyFailure, (state, { error }) => ({
    ...state,
    deleting: false,
    deleteError: error
  })),

  // Regenerate API Key
  on(ApiKeysActions.regenerateApiKey, (state) => ({
    ...state,
    regenerating: true,
    createError: null,
    newlyCreatedKey: null
  })),

  on(ApiKeysActions.regenerateApiKeySuccess, (state, { response }) => ({
    ...state,
    regenerating: false,
    newlyCreatedKey: response,
    createError: null
  })),

  on(ApiKeysActions.regenerateApiKeyFailure, (state, { error }) => ({
    ...state,
    regenerating: false,
    createError: error
  })),

  // Rotate API Key
  on(ApiKeysActions.rotateApiKey, (state) => ({
    ...state,
    rotating: true,
    rotateError: null,
    rotatedKey: null
  })),

  on(ApiKeysActions.rotateApiKeySuccess, (state, { response }) => ({
    ...state,
    rotating: false,
    rotatedKey: response,
    apiKeys: state.apiKeys.map(key =>
      key.id === response.old_key_id
        ? { ...key, is_active: !response.old_key_revoked }
        : key
    ),
    rotateError: null
  })),

  on(ApiKeysActions.rotateApiKeyFailure, (state, { error }) => ({
    ...state,
    rotating: false,
    rotateError: error
  })),

  // Load Usage Stats
  on(ApiKeysActions.loadApiKeyUsageStats, (state) => ({
    ...state,
    loadingUsageStats: true,
    usageStatsError: null
  })),

  on(ApiKeysActions.loadApiKeyUsageStatsSuccess, (state, { stats }) => ({
    ...state,
    loadingUsageStats: false,
    usageStats: stats,
    usageStatsError: null
  })),

  on(ApiKeysActions.loadApiKeyUsageStatsFailure, (state, { error }) => ({
    ...state,
    loadingUsageStats: false,
    usageStatsError: error
  })),

  // Update Filters
  on(ApiKeysActions.updateFilters, (state, { filters }) => ({
    ...state,
    filters: { ...state.filters, ...filters }
  })),

  // Clear State
  on(ApiKeysActions.clearApiKeys, () => initialState),

  on(ApiKeysActions.clearSelectedApiKey, (state) => ({
    ...state,
    selectedApiKey: null
  })),

  on(ApiKeysActions.clearNewlyCreatedKey, (state) => ({
    ...state,
    newlyCreatedKey: null
  })),

  on(ApiKeysActions.clearRotatedKey, (state) => ({
    ...state,
    rotatedKey: null
  }))
);

